import { useMemo, useState } from 'react';
import { Alert, Badge, Group, Paper, Select, Stack, Table, Text, Title } from '@mantine/core';
import { IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { ReportPage } from '../components/ReportPage';
import { useCustomers, useDispatchesForGenealogy, useGenealogyReport } from '../api';
import { formatDateTime, formatKg, formatQty } from '../format';

export interface GenealogyReportProps {
  initialDispatchId?: string;
}

/** Report #12 (BUILD_SPEC.md §9.7, §13) — a lookup form by dispatch (goal text):
 * pick a posted dispatch, see each line's time-windowed provenance (the receipts
 * feeding its source silo's stage chain since that silo was last empty). */
export function GenealogyReport({ initialDispatchId }: GenealogyReportProps) {
  const [dispatchId, setDispatchId] = useState<string | null>(initialDispatchId ?? null);
  const dispatchesQuery = useDispatchesForGenealogy();
  const customersQuery = useCustomers();
  const query = useGenealogyReport(dispatchId ?? undefined);

  const customerNameById = useMemo(
    () => new Map((customersQuery.data?.items ?? []).map((c) => [c.id, c.name])),
    [customersQuery.data],
  );

  const dispatchOptions = useMemo(
    () =>
      (dispatchesQuery.data?.items ?? []).map((d) => ({
        value: d.id,
        label: `${d.number} — ${customerNameById.get(d.customerId) ?? d.customerId} — ${formatDateTime(d.dispatchedAt)}`,
      })),
    [dispatchesQuery.data, customerNameById],
  );

  return (
    <ReportPage
      title="Genealogy"
      description="Time-windowed provenance of an outbound load (§9.7): the receipts feeding the source silo's stage chain since it was last empty."
      action={
        dispatchId ? (
          <CsvDownloadButton
            path="/reporting/genealogy"
            query={{ dispatchId }}
            filename="genealogy.csv"
            description="Exports this dispatch's full genealogy — every receipt feeding it — as a CSV file"
          />
        ) : undefined
      }
    >
      <Select
        label="Dispatch"
        placeholder="Choose a posted dispatch…"
        searchable
        clearable
        data={dispatchOptions}
        value={dispatchId}
        onChange={setDispatchId}
        w={420}
        nothingFoundMessage={dispatchesQuery.isLoading ? 'Loading…' : 'No posted dispatches found'}
        comboboxProps={{ withinPortal: true }}
      />

      {!dispatchId && (
        <Alert color="graphite" variant="light" icon={<IconInfoCircle size={16} />}>
          Choose a dispatch above to see its genealogy.
        </Alert>
      )}

      {dispatchId && query.isLoading && <Text c="dimmed">Loading genealogy…</Text>}

      {dispatchId && query.isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load genealogy for this dispatch.
        </Alert>
      )}

      {query.data && (
        <Stack gap="lg">
          <Text size="sm" c="dimmed">
            Dispatch <strong>{query.data.dispatchNumber}</strong>, dispatched {formatDateTime(query.data.dispatchedAt)}.
          </Text>

          {query.data.lines.map((line) => (
            <Paper key={line.lineId} withBorder radius="md" p="md">
              <Stack gap="xs">
                <Group justify="space-between">
                  <Title order={4}>
                    Line {line.lineId.slice(-6)} — {formatQty(line.qty)} kg
                  </Title>
                  {line.windowStart && (
                    <Badge variant="light" color="graphite">
                      Window since {formatDateTime(line.windowStart)}
                    </Badge>
                  )}
                </Group>

                {line.feedingReceipts.length === 0 ? (
                  <Text c="dimmed" size="sm">
                    No receipts found feeding this line within the recorded window.
                  </Text>
                ) : (
                  <Table.ScrollContainer minWidth={480}>
                    <Table verticalSpacing="xs">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Received</Table.Th>
                          <Table.Th>Goods receipt</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Qty</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>Dry kg</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {line.feedingReceipts.map((r) => (
                          <Table.Tr key={r.movementId}>
                            <Table.Td>{formatDateTime(r.occurredAt)}</Table.Td>
                            <Table.Td>{r.goodsReceiptNumber ?? '—'}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{formatQty(r.qty)}</Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>{formatKg(r.dryKg)}</Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </ReportPage>
  );
}
